import {socialAuthIntegrationSchema} from '@practicegenius/domain/src/data/integrations';
import {z} from 'zod';
import {replaceSubdomainInUrl} from '@practicegenius/pg-util/src/UrlUtils';
import qs from 'qs';
import {authPerspectiveSchema} from './authPerspective';

/** Subdomain of Central Identity Service */
const ID_SUBDOMAIN = 'id';

export const idDotLoginRoutes = {
  index: '/login',
  social: {
    login: '/login/social_provider/:provider',
    callback: '/login/social_provider/callback',
  },
};

export const idDotRegistrationRoutes = {
  authenticate: '/registration/authenticate',
};

export const idDotApiRoutesForLogin = {
  authRefresh: '/api/s2s/authRefresh',
  confirmLogin: '/api/s2s/confirmLogin',
};

export const idDotLogoutRoutes = {
  logout: {
    index: '/app/logout',
    initiate: '/app/logout/initiate',
  },
};

export const idDotUserRoutes = {
  profile: '/app/user/profile',
  linkedFamily: '/app/user/linked-family',
};

const loginErrorQueryParamSchema = z.union([
  z.object({
    errorType: z.literal('unregistered_social_account'),
    provider: socialAuthIntegrationSchema,
  }),
  z.object({
    errorType: z.literal('generic'),
  }),
  z.object({
    errorType: z.literal('registration_not_finished'),
    token: z.string(),
    viaSocial: z.literal('1').optional(),
  }),
  z.object({}),
]);

export const loginRejectAfterSuccessReasonSchema = z.literal('denied');
export type LoginRejectAfterSuccessReason = z.infer<
  typeof loginRejectAfterSuccessReasonSchema
>;

export const idDotLoginQueryParamsSchema = z.intersection(
  loginErrorQueryParamSchema,
  z.object({
    referrer: z.string().optional(),
    loginPerspective: authPerspectiveSchema.optional(),
    rejectAfterSuccess: loginRejectAfterSuccessReasonSchema.optional(),
  })
);

export const buildIdBaseUrlFromCurrentUrl = (currentUrl: string) =>
  replaceSubdomainInUrl(currentUrl, ID_SUBDOMAIN);

/**
 * Generic function to build ID dot URLs for any route
 */
export const buildIdDotUrl = (
  currentUrl: string,
  route: string,
  queryParams: Record<string, string | number | boolean> = {}
): string => {
  const idUrl = buildIdBaseUrlFromCurrentUrl(currentUrl);
  idUrl.pathname = route;
  if (Object.keys(queryParams).length > 0) {
    idUrl.search = qs.stringify(queryParams);
  }
  return idUrl.toString();
};

export type IdDotLoginQueryParams = z.infer<typeof idDotLoginQueryParamsSchema>;

export const buildIdLoginUrlFromCurrentUrl = (
  currentUrl: string,
  params: IdDotLoginQueryParams
): string => {
  const loginUrl = buildIdBaseUrlFromCurrentUrl(currentUrl);
  loginUrl.pathname = idDotLoginRoutes.index;
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      loginUrl.searchParams.set(key, value);
    }
  }
  return loginUrl.toString();
};

export const idDotLogoutQueryParamsSchema = z.object({
  /** Perspective to show on the login screen after logout */
  loginPerspective: authPerspectiveSchema.optional(),
});
export type IdDotLogoutQueryParams = z.infer<
  typeof idDotLogoutQueryParamsSchema
>;

export const buildIdLogoutUrlFromCurrentUrl = (
  currentUrl: string,
  params: IdDotLogoutQueryParams = {}
): string => {
  const logoutUrl = buildIdBaseUrlFromCurrentUrl(currentUrl);
  logoutUrl.pathname = idDotLogoutRoutes.logout.index;
  logoutUrl.search = qs.stringify(params);
  return logoutUrl.toString();
};

export const idDotRegistrationAuthCodeParamsSchema = z.object({
  registrationAuthCode: z.string(),
});
export type IdDotRegistrationAuthCodeParams = z.infer<
  typeof idDotRegistrationAuthCodeParamsSchema
>;

export const buildIdRegistrationUrl = (
  currentUrl: string,
  params: IdDotRegistrationAuthCodeParams
): string => {
  const registrationUrl = buildIdBaseUrlFromCurrentUrl(currentUrl);
  registrationUrl.pathname = idDotRegistrationRoutes.authenticate;
  registrationUrl.search = qs.stringify(params);
  return registrationUrl.toString();
};
